// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT.
//
// Copied from shared/consent-ui/PrivacySettings.jsx by shared/sync-consent-ui.js.
// Edit the original and re-run the sync; changes made here are overwritten and
// will silently un-share this component from the other NexusAI apps.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Loader2, ExternalLink, Download, Trash2 } from './icons';
import { useConsentConfig } from './ConsentProvider';

/**
 * PrivacySettings — where a person actually exercises control.
 *
 * Everything else in the Consent Manager collects consent at the moment it's
 * needed. This is the one place they can see what they've agreed to and change
 * their mind, which is what makes the rest of it meaningful rather than a
 * one-way collection funnel.
 *
 * Three deliberate honesty rules, all of which cost something and are worth it:
 *
 *  1. Consents that CANNOT be switched off are still shown, with the reason.
 *     Hiding them would imply this list is everything the platform processes.
 *     Rendering them as dead toggles would imply a control that doesn't exist.
 *
 *  2. When withdrawing won't actually delete anything yet (revokeIsImmediate
 *     false — the deletion handler isn't built), the confirm says so. Implying
 *     erasure that hasn't happened is the worst thing a screen like this can do.
 *
 *  3. Resource-scoped consents (per batch, per application) are shown read-only.
 *     One global switch cannot honestly represent a dozen separate answers, and
 *     pretending otherwise would silently revoke things the user didn't mean to.
 *
 * Requires `api.getManageable`, `api.grantConsent` and `api.revokeConsent` on
 * the ConsentProvider.
 */
// How a consent was captured, in words a person would use about themselves.
// The raw enum ("google_oauth", "migration") is accurate but reads like a log
// file; this is the same fact said plainly.
const METHOD_LABEL = {
  self: 'You chose this',
  google_oauth: 'During Google sign-up',
  linkedin_oauth: 'During LinkedIn sign-up',
  admin: 'Recorded for you by our team',
  system: 'Applied automatically',
  migration: 'Carried over from your earlier settings',
};

// Resource-scoped rows answer for one batch/application, not the whole account.
// Saying which is the difference between a log and an audit trail.
const SCOPE_LABEL = {
  BATCH: 'for one batch',
  APPLICATION: 'for one application',
  SESSION: 'for one session',
  IDEA: 'for one idea',
  POSTING: 'for one posting',
  ACCOUNT: 'for your account',
  CHALLENGE: 'for one challenge',
};

const formatWhen = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function PrivacySettings({ className = '' }) {
  const { api, theme: t } = useConsentConfig();

  const [data, setData] = useState({ manageable: [], required: [] });
  const [history, setHistory] = useState([]);
  const [historyState, setHistoryState] = useState('loading'); // loading | ready | unavailable
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyCode, setBusyCode] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // ── Data rights (export / erasure) ────────────────────────────────────────
  // Both are OPTIONAL props, same convention as every other api.* function
  // here — StartupNexus (founder accounts) and MentorNexus/ExpertNexus
  // (mentor accounts) don't wire these in because the backend endpoint is
  // Student-only; the section below simply doesn't render for them rather
  // than showing a button that would always fail.
  const [exporting, setExporting] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [erasePassword, setErasePassword] = useState('');
  const [eraseConfirmText, setEraseConfirmText] = useState('');
  const [erasing, setErasing] = useState(false);
  const [eraseError, setEraseError] = useState('');
  const [erased, setErased] = useState(false);

  const exportData = async () => {
    setExporting(true);
    setError('');
    try {
      const res = await api.exportMyData();
      // Client-side download — this is the person's own data, handed to them
      // directly rather than emailed or staged server-side.
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Could not export your data.');
    } finally {
      setExporting(false);
    }
  };

  const submitErasure = async () => {
    setEraseError('');
    if (eraseConfirmText.trim().toUpperCase() !== 'DELETE') {
      setEraseError('Type DELETE to confirm.');
      return;
    }
    if (!erasePassword) {
      setEraseError('Enter your current password.');
      return;
    }
    setErasing(true);
    try {
      await api.requestErasure({ password: erasePassword });
      setErased(true);
      setEraseOpen(false);
    } catch (e) {
      setEraseError(e?.message || 'Could not process your request.');
    } finally {
      setErasing(false);
      setErasePassword('');
    }
  };

  // The trail is loaded separately and never blocks the controls. If history
  // fails, a person can still change their mind — which matters more than the
  // record of having done so.
  const loadHistory = useCallback(async () => {
    if (!api.getHistory) { setHistoryState('unavailable'); return; }
    try {
      const res = await api.getHistory({ limit: 200 });
      setHistory(Array.isArray(res?.history) ? res.history : []);
      setHistoryState('ready');
    } catch {
      setHistoryState('unavailable');
    }
  }, [api]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!api.getManageable) throw new Error('Privacy settings are not configured for this app.');
      const res = await api.getManageable();
      setData({ manageable: res?.manageable || [], required: res?.required || [] });
    } catch (e) {
      setError(e?.message || 'Could not load your privacy settings.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);

  const toggle = async (item) => {
    setError('');
    setNotice('');

    if (item.granted) {
      const caveat = item.revokeIsImmediate
        ? ''
        : '\n\nNote: withdrawing is recorded immediately, but removing the data already processed ' +
          'under this consent is handled separately and may not be instant.';
      if (!window.confirm(`Withdraw consent for "${item.title}"?${caveat}`)) return;
    }

    setBusyCode(item.code);
    try {
      if (item.granted) {
        const res = await api.revokeConsent({ code: item.code });
        setNotice(res?.message || 'Your consent has been withdrawn.');
      } else {
        await api.grantConsent({ code: item.code, accepted: true, uniqueId: item.uniqueId });
        setNotice('Thanks — that’s saved.');
      }
      await Promise.all([load(), loadHistory()]);
    } catch (e) {
      // A refusal here is often legitimate (payment consent can't be withdrawn),
      // so show the server's reason rather than a generic failure.
      setError(e?.message || 'Could not update that setting.');
    } finally {
      setBusyCode(null);
    }
  };

  if (loading) {
    return (
      <div className={`flex justify-center py-16 ${className}`}>
        <Loader2 size={28} className={`animate-spin ${t.accent}`} />
      </div>
    );
  }

  // After a successful erasure the account no longer functions — the next
  // authenticated call would 401/440 anyway, so stop rendering settings that
  // imply there's still an account to manage and say plainly what happened.
  if (erased) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className={`mt-0.5 inline-flex rounded-full p-2 ${t.accentBg} ${t.accent}`}>
          <ShieldCheck size={18} />
        </div>
        <h2 className={`text-lg font-semibold ${t.text}`}>Your account has been closed</h2>
        <p className={`text-sm ${t.textMuted}`}>
          Your personal information has been anonymized and you&apos;ve been signed out
          everywhere. This page won&apos;t work again for this account.
        </p>
      </div>
    );
  }

  // Codes come back on log rows; titles live on the settings payload. Joining
  // them here keeps the log human-readable without a second backend lookup.
  const titleFor = (code) => {
    const hit =
      data.manageable.find((i) => i.code === code) ||
      data.required.find((i) => i.code === code);
    return hit?.title || code;
  };

  const visibleHistory = showAll ? history : history.slice(0, 12);

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 rounded-full p-2 ${t.accentBg} ${t.accent}`}>
          <ShieldCheck size={18} />
        </div>
        <div>
          <h2 className={`text-lg font-semibold ${t.text}`}>Privacy &amp; consent</h2>
          <p className={`mt-1 text-sm ${t.textMuted}`}>
            What you&apos;ve agreed to, and what you can change. Turning something off here takes
            effect straight away and never affects your access to the platform.
          </p>
        </div>
      </div>

      {notice ? (
        <p className={`rounded-lg px-3 py-2 text-xs ${t.accentBg} ${t.accent}`}>{notice}</p>
      ) : null}
      {error ? (
        <p className={`rounded-lg px-3 py-2 text-xs ${t.errorBox}`}>{error}</p>
      ) : null}

      {/* Controls left, the record of using them right — side by side once
          there's genuinely enough room, stacked otherwise. This uses flex-wrap
          driven by each column's own min-width rather than a `lg:` grid split,
          because that split responds to the VIEWPORT, not this component's own
          container — and this component gets embedded in containers of very
          different widths across the six apps that vendor it (some settings
          pages give it the full page, others a narrow single column). A grid
          keyed to viewport width forced the two-column layout on even in a
          narrow container, squeezing the log to its fixed 22rem and leaving
          next to nothing for the controls column — down to single words
          wrapping one per line. flex-wrap with min-width wraps based on the
          space actually available, wherever this ends up mounted. */}
      <div className="flex flex-wrap gap-6 items-start">
      <div className="min-w-[280px] flex-[2] basis-[320px] space-y-6">

      {/* ── Things they control ─────────────────────────────────────────────── */}
      <div className="space-y-3">
        {data.manageable.length === 0 && (
          <p className={`text-sm ${t.textFaint}`}>
            There&apos;s nothing to manage here yet.
          </p>
        )}

        {data.manageable.map((item) => (
          <div key={item.code} className={`rounded-xl border p-4 ${t.border}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className={`text-sm font-medium ${t.text}`}>{item.title}</div>
                <p className={`mt-1 text-xs leading-relaxed ${t.textMuted}`}>{item.description}</p>

                {item.lastAnswered ? (
                  <p className={`mt-2 text-[11px] ${t.textFaint}`}>
                    {item.granted ? 'Agreed' : 'Last changed'}{' '}
                    <span className={`rounded px-1 py-0.5 font-semibold tabular-nums ${t.accentBg} ${t.accent}`}>
                      {formatWhen(item.lastAnswered)}
                    </span>
                    {item.agreedVersion ? ` · version ${item.agreedVersion.split('.')[1] || ''}` : ''}
                  </p>
                ) : (
                  <p className={`mt-2 text-[11px] ${t.textFaint}`}>You haven&apos;t been asked this yet.</p>
                )}
              </div>

              {item.resourceScoped ? (
                // Answered separately per batch/application — a single switch
                // here would misrepresent several distinct decisions.
                <span className={`shrink-0 text-[11px] ${t.textFaint}`} title="Answered separately each time">
                  Per item
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => toggle(item)}
                  disabled={busyCode === item.code}
                  aria-pressed={item.granted}
                  className={`shrink-0 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
                    item.granted
                      ? `border ${t.border} ${t.textMuted} ${t.ghostHover}`
                      : `text-white ${t.accentButton}`
                  }`}
                >
                  {busyCode === item.code ? <Loader2 size={12} className="animate-spin" /> : null}
                  {item.granted ? 'Turn off' : 'Turn on'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Things they don't control, and why ──────────────────────────────── */}
      {data.required.length > 0 && (
        <div className="space-y-3">
          <h3 className={`text-sm font-medium ${t.text}`}>Always on</h3>
          <p className={`text-xs ${t.textMuted}`}>
            These can&apos;t be switched off while you have an account — they&apos;re what let us
            run it. Listed so you can see them, not because there&apos;s a choice to make.
          </p>
          {data.required.map((item) => (
            <div key={item.code} className={`rounded-xl border p-4 opacity-80 ${t.border}`}>
              <div className={`text-sm font-medium ${t.text}`}>{item.title}</div>
              <p className={`mt-1 text-xs leading-relaxed ${t.textMuted}`}>{item.description}</p>
              <p className={`mt-2 text-[11px] ${t.textFaint}`}>{item.reason}</p>
            </div>
          ))}
        </div>
      )}

      </div>{/* /left column */}

      {/* ── The complete trail ──────────────────────────────────────────────── */}
      <aside className={`min-w-[280px] flex-1 basis-[280px] max-w-[26rem] rounded-xl border ${t.border} lg:sticky lg:top-6`}>
        <div className={`border-b px-4 py-3 ${t.border}`}>
          <div className="flex items-baseline justify-between gap-2">
            <h3 className={`text-sm font-medium ${t.text}`}>Your consent log</h3>
            {historyState === 'ready' && history.length > 0 ? (
              <span className={`text-[11px] tabular-nums ${t.textFaint}`}>
                {history.length} {history.length === 1 ? 'entry' : 'entries'}
              </span>
            ) : null}
          </div>
          <p className={`mt-1 text-[11px] leading-relaxed ${t.textMuted}`}>
            Every time you agreed to something or withdrew it, newest first. This record is
            permanent — withdrawing a consent adds an entry, it never removes one.
          </p>
        </div>

        {historyState === 'loading' ? (
          <div className="flex justify-center py-10">
            <Loader2 size={18} className={`animate-spin ${t.accent}`} />
          </div>
        ) : historyState === 'unavailable' ? (
          <p className={`px-4 py-6 text-xs ${t.textFaint}`}>
            Your log couldn&apos;t be loaded just now. Your settings above still work — try
            refreshing the page.
          </p>
        ) : history.length === 0 ? (
          <p className={`px-4 py-6 text-xs ${t.textFaint}`}>
            Nothing recorded yet. Entries appear here as soon as you answer a consent request.
          </p>
        ) : (
          <>
            <ol className={`divide-y ${t.border}`}>
              {visibleHistory.map((row, i) => {
                const withdrawn = row.status === 'REVOKED';
                const declined = row.status === 'DENIED';
                const scope = row.resource?.type && row.resource.type !== 'GLOBAL'
                  ? SCOPE_LABEL[row.resource.type]
                  : null;

                return (
                  <li key={`${row.consentCode}-${row.actionDate}-${i}`} className="px-4 py-3">
                    <div className="flex items-start gap-2.5">
                      {/* Colour alone never carries the meaning — the verb does. */}
                      <span
                        aria-hidden="true"
                        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-current ${
                          withdrawn || declined ? `${t.textFaint} opacity-50` : t.accent
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs font-medium leading-snug ${t.text}`}>
                          {withdrawn ? 'Withdrew' : declined ? 'Declined' : 'Agreed to'}{' '}
                          {titleFor(row.consentCode)}
                          {scope ? <span className={`font-normal ${t.textMuted}`}> {scope}</span> : null}
                        </p>
                        {/* When it happened is the point of an audit trail, so it
                            gets the emphasis rather than sitting in fine print. */}
                        <p
                          className={`mt-1.5 inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${t.accentBg} ${t.accent}`}
                        >
                          {formatWhen(row.actionDate)}
                        </p>
                        <p className={`mt-1 text-[11px] ${t.textFaint}`}>
                          {METHOD_LABEL[row.method] || METHOD_LABEL.self}
                          {row.platform ? ` · on ${row.platform}` : ''}
                          {row.agreedVersion ? ` · v${row.agreedVersion.split('.').slice(-3).join('.')}` : ''}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            {history.length > 12 ? (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className={`w-full border-t px-4 py-2.5 text-[11px] font-medium transition ${t.border} ${t.accent} ${t.ghostHover}`}
              >
                {showAll ? 'Show recent only' : `Show all ${history.length} entries`}
              </button>
            ) : null}
          </>
        )}
      </aside>
      </div>{/* /flex row */}

      {/* ── Data rights ────────────────────────────────────────────────────
          Renders self-service export/erasure only when the app actually
          wires api.exportMyData/api.requestErasure in — omitted entirely
          for founder/mentor-backed apps rather than showing a button that
          would always fail. The mailto fallback below still covers them,
          and still covers anything these two actions don't (individual
          submission records, etc — see the export payload's own note). */}
      {(api.exportMyData || api.requestErasure) && (
        <div className={`rounded-xl border p-4 space-y-3 ${t.border}`}>
          <h3 className={`text-sm font-medium ${t.text}`}>Your data</h3>
          <div className="flex flex-wrap gap-2">
            {api.exportMyData && (
              <button
                type="button"
                onClick={exportData}
                disabled={exporting}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${t.border} ${t.textMuted} ${t.ghostHover}`}
              >
                {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                Download my data
              </button>
            )}
            {api.requestErasure && (
              <button
                type="button"
                onClick={() => { setEraseOpen(true); setEraseError(''); setEraseConfirmText(''); }}
                className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/40"
              >
                <Trash2 size={12} />
                Delete my account
              </button>
            )}
          </div>
        </div>
      )}

      <p className={`text-[11px] leading-relaxed ${t.textFaint}`}>
        {api.exportMyData || api.requestErasure ? 'For anything the buttons above don’t cover, contact' : 'To request a copy of your data or ask for your account to be closed, contact'}{' '}
        <a href="mailto:privacy@nexusaiweb.com" className={`hover:underline ${t.accent}`}>
          privacy@nexusaiweb.com
        </a>
        .{' '}
        <a
          href={t.privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-0.5 hover:underline ${t.accent}`}
        >
          Privacy policy <ExternalLink size={10} />
        </a>
      </p>

      {/* ── Erasure confirm ─────────────────────────────────────────────── */}
      {eraseOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className={`w-full max-w-sm rounded-2xl border p-5 space-y-4 ${t.surface} ${t.border}`}>
            <div>
              <h3 className={`text-base font-semibold ${t.text}`}>Delete your account?</h3>
              <p className={`mt-1.5 text-xs leading-relaxed ${t.textMuted}`}>
                This anonymizes your personal information (name, email, phone, date of birth, and
                similar fields) and signs you out everywhere. It cannot be undone. Records we&apos;re
                required to keep for financial or legal reasons (payments, certificates) are not deleted.
              </p>
            </div>

            {eraseError ? (
              <p className={`rounded-lg px-3 py-2 text-xs ${t.errorBox}`}>{eraseError}</p>
            ) : null}

            <div>
              <label className={`text-xs ${t.textMuted}`}>Current password</label>
              <input
                type="password"
                value={erasePassword}
                onChange={(e) => setErasePassword(e.target.value)}
                className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${t.border} ${t.surface} ${t.text}`}
              />
            </div>
            <div>
              <label className={`text-xs ${t.textMuted}`}>Type DELETE to confirm</label>
              <input
                type="text"
                value={eraseConfirmText}
                onChange={(e) => setEraseConfirmText(e.target.value)}
                className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${t.border} ${t.surface} ${t.text}`}
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setEraseOpen(false)}
                className={`px-3 py-1.5 text-xs ${t.textMuted} ${t.ghostHover} rounded-lg`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitErasure}
                disabled={erasing}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {erasing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Delete my account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
